/** Utilitaires luminance / contraste pour le thème player institution. */

const HEX_RE = /#([0-9a-fA-F]{3,8})\b/;

export function extractFirstHex(color: string): string | null {
  const m = color.trim().match(HEX_RE);
  if (!m) return null;
  let hex = m[1];
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (hex.length === 8) hex = hex.slice(0, 6);
  return `#${hex.toLowerCase()}`;
}

function parseRgbChannels(color: string): [number, number, number] | null {
  const hex = extractFirstHex(color);
  if (hex) {
    const h = hex.slice(1);
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  }
  const rgb = color.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (rgb) {
    return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  }
  return null;
}

/** Luminance relative WCAG (0 = noir, 1 = blanc). */
export function relativeLuminance(color: string): number | null {
  const ch = parseRgbChannels(color);
  if (!ch) return null;
  const [r, g, b] = ch.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function isColorDark(color: string, threshold = 0.42): boolean {
  const lum = relativeLuminance(color);
  if (lum == null) {
    const lower = color.toLowerCase();
    if (lower.includes('0c1c38') || lower.includes('061128') || lower.includes('19335e')) return true;
    return false;
  }
  return lum < threshold;
}

export function contrastRatio(fg: string, bg: string): number | null {
  const l1 = relativeLuminance(fg);
  const l2 = relativeLuminance(bg);
  if (l1 == null || l2 == null) return null;
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

export function hasReadableContrast(fg: string, bg: string, minRatio = 4.5): boolean {
  const ratio = contrastRatio(fg, bg);
  if (ratio == null) return true;
  return ratio >= minRatio;
}

export function lightenHex(hex: string, amount = 0.45): string {
  const normalized = extractFirstHex(hex) ?? '#001B3D';
  const h = normalized.slice(1);
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  return `#${mix(r).toString(16).padStart(2, '0')}${mix(g).toString(16).padStart(2, '0')}${mix(b).toString(16).padStart(2, '0')}`;
}

export function pickReadableTextColor(
  background: string,
  preferred?: string,
  lightFallback = '#f3f4f6',
  darkFallback = '#111827',
): string {
  const bgDark = isColorDark(background);
  if (preferred?.trim()) {
    const pref = preferred.trim();
    if (hasReadableContrast(pref, background)) return pref;
  }
  return bgDark ? lightFallback : darkFallback;
}

export function pickReadableMutedColor(
  background: string,
  preferred?: string,
  lightFallback = '#b6c4d9',
  darkFallback = '#6b7280',
): string {
  return pickReadableTextColor(background, preferred, lightFallback, darkFallback);
}
